import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ApiResponse, RequestParams } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import type {
  RoleBase,
  RoleById,
  RoleCreatePayload,
  RoleUpdatePayload,
  PermissionsResponse,
  RoleItem,
  RoleItemListParams,
} from './types';
import { ROLE_KEYS } from '../lib';
import { RoleApi } from '../api';

export const useRoleList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<RoleBase>>({
    queryKey: ROLE_KEYS.list(params),
    queryFn: () => RoleApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useRoleById = (id: number) => {
  return useQuery<ApiResponse<RoleById>>({
    queryKey: ROLE_KEYS.detail(id),
    queryFn: () => RoleApi.getById(id),
    enabled: !!id,
  });
};

export const useRoleItemList = (params?: RoleItemListParams, enabled: boolean = true) => {
  return useQuery<ApiResponse<RoleItem[]>, Error, RoleItem[] | undefined>({
    queryKey: ROLE_KEYS.itemList(params),
    queryFn: () => RoleApi.getItemList(params),
    enabled: enabled && params !== undefined,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const usePermissions = () => {
  return useQuery<ApiResponse<PermissionsResponse>>({
    queryKey: ROLE_KEYS.permissions(),
    queryFn: () => RoleApi.getPermissions(),
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RoleCreatePayload) => RoleApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROLE_KEYS.all });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RoleUpdatePayload) => RoleApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROLE_KEYS.all });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => RoleApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROLE_KEYS.all });
    },
  });
};
